import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { Palette, User, Lock, Bell, Trash2, Gift, Copy, Check } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useThemeStore } from '@/store/themeStore'
import { useAuthStore } from '@/store/authStore'
import { MatrixRain } from '@/components/ui/MatrixRain'
import { apiClient } from '@/services/api'
import { useTerminology } from '@/hooks/useTerminology'
import styles from './Settings.module.css'

export function Settings() {
  const { theme, toggleTheme } = useThemeStore()
  const { user, setUser, logout } = useAuthStore()
  const navigate = useNavigate()
  const t = useTerminology()

  // Pirate Name
  const [displayName, setDisplayName] = useState(user?.display_name || '')
  const [nameSaving, setNameSaving] = useState(false)
  const [nameMsg, setNameMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null)

  // Password
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [pwSaving, setPwSaving] = useState(false)
  const [pwMsg, setPwMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null)

  // Newsletter
  const [subscribed, setSubscribed] = useState(false)
  const [nlLoading, setNlLoading] = useState(true)
  const [nlUnavailable, setNlUnavailable] = useState(false)
  const [nlToggling, setNlToggling] = useState(false)

  // Referral
  const [referralCopied, setReferralCopied] = useState(false)
  const [referralStats, setReferralStats] = useState<{ total_referrals: number; credits_earned: number } | null>(null)
  const [alreadyReferred, setAlreadyReferred] = useState(true) // default true to hide until loaded
  const [referralCodeInput, setReferralCodeInput] = useState('')
  const [referralSubmitting, setReferralSubmitting] = useState(false)
  const [referralMsg, setReferralMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null)

  // Delete Account
  const [deletePassword, setDeletePassword] = useState('')
  const [deleteConfirmText, setDeleteConfirmText] = useState('')
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [deleteLoading, setDeleteLoading] = useState(false)
  const [deleteError, setDeleteError] = useState('')

  // Sync display name when user changes
  useEffect(() => {
    if (user?.display_name) setDisplayName(user.display_name)
  }, [user?.display_name])

  // Fetch referral stats on mount
  useEffect(() => {
    apiClient.getReferralInfo()
      .then(res => {
        setReferralStats({
          total_referrals: res.data.total_referrals,
          credits_earned: res.data.credits_earned
        })
        setAlreadyReferred(res.data.already_referred)
      })
      .catch(() => {})
  }, [])

  // Fetch newsletter status on mount
  useEffect(() => {
    apiClient.getNewsletterStatus()
      .then(res => {
        setSubscribed(res.data.subscribed)
        setNlLoading(false)
      })
      .catch(err => {
        if (err.response?.status === 503) {
          setNlUnavailable(true)
        }
        setNlLoading(false)
      })
  }, [])

  // --- Handlers ---

  const handleSaveName = async () => {
    setNameSaving(true)
    setNameMsg(null)
    try {
      await apiClient.updateProfile({ display_name: displayName })
      if (user) {
        const updated = { ...user, display_name: displayName }
        setUser(updated)
        localStorage.setItem('cached_user', JSON.stringify(updated))
      }
      setNameMsg({ type: 'success', text: 'Pirate name updated!' })
    } catch {
      setNameMsg({ type: 'error', text: 'Failed to update name' })
    } finally {
      setNameSaving(false)
    }
  }

  const handleChangePassword = async () => {
    setPwMsg(null)
    if (newPassword.length < 8) {
      setPwMsg({ type: 'error', text: 'Password must be at least 8 characters' })
      return
    }
    if (newPassword !== confirmPassword) {
      setPwMsg({ type: 'error', text: 'Passwords do not match' })
      return
    }
    setPwSaving(true)
    try {
      await apiClient.changePassword(currentPassword, newPassword)
      setCurrentPassword('')
      setNewPassword('')
      setConfirmPassword('')
      setPwMsg({ type: 'success', text: 'Password changed successfully!' })
    } catch (err: any) {
      if (err.response?.status === 401) {
        setPwMsg({ type: 'error', text: 'Current password is incorrect' })
      } else {
        setPwMsg({ type: 'error', text: err.response?.data?.message || 'Failed to change password' })
      }
    } finally {
      setPwSaving(false)
    }
  }

  const handleToggleNewsletter = async () => {
    setNlToggling(true)
    try {
      if (subscribed) {
        await apiClient.unsubscribeNewsletter()
        setSubscribed(false)
      } else {
        await apiClient.subscribeNewsletter()
        setSubscribed(true)
      }
    } catch {
      // silently fail, toggle reverts
    } finally {
      setNlToggling(false)
    }
  }

  const handleDeleteAccount = async () => {
    setDeleteError('')
    setDeleteLoading(true)
    try {
      const hasPassword = user?.has_password !== false
      await apiClient.deleteAccount(
        hasPassword ? { password: deletePassword } : { confirmation: deleteConfirmText }
      )
      await logout()
      navigate('/')
    } catch (err: any) {
      if (err.response?.status === 401) {
        setDeleteError('Incorrect password')
      } else {
        setDeleteError(err.response?.data?.message || 'Failed to delete account')
      }
    } finally {
      setDeleteLoading(false)
    }
  }

  const handleSubmitReferralCode = async () => {
    setReferralMsg(null)
    if (!referralCodeInput.trim()) return
    setReferralSubmitting(true)
    try {
      const res = await apiClient.submitReferralCode(referralCodeInput.trim())
      setReferralMsg({ type: 'success', text: res.data.message })
      setAlreadyReferred(true)
      setReferralCodeInput('')
    } catch (err: any) {
      setReferralMsg({ type: 'error', text: err.response?.data?.message || 'Failed to apply referral code' })
    } finally {
      setReferralSubmitting(false)
    }
  }

  const nameUnchanged = displayName === (user?.display_name || '')

  return (
    <div className={styles.container}>
      <MatrixRain />

      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className={styles.header}
      >
        <h1 className={styles.title}>SETTINGS</h1>
        <p className={styles.subtitle}>Configuration & Account Management</p>
      </motion.div>

      <div className={styles.settingsGrid}>

        {/* Section 1: Theme */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className={styles.settingsCard}
        >
          <div className={styles.cardContent}>
            <h2 className={styles.cardTitle}>
              <Palette className="inline w-5 h-5 mr-2" />
              INTERFACE THEME
            </h2>
            <div className={styles.toggleGroup}>
              <span className={styles.toggleLabel}>Dark Mode</span>
              <div
                className={`${styles.toggle} ${theme === 'dark' ? styles.active : ''}`}
                onClick={toggleTheme}
              />
            </div>
          </div>
        </motion.div>

        {/* Section 2: Pirate Name */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className={styles.settingsCard}
        >
          <div className={styles.cardContent}>
            <h2 className={styles.cardTitle}>
              <User className="inline w-5 h-5 mr-2" />
              PIRATE NAME
            </h2>
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>Display Name</label>
              <input
                type="text"
                className={styles.formInput}
                value={displayName}
                onChange={(e) => { setDisplayName(e.target.value); setNameMsg(null) }}
                placeholder="Enter your pirate name..."
              />
            </div>
            <button
              className={styles.primaryButton}
              onClick={handleSaveName}
              disabled={nameUnchanged || nameSaving}
            >
              {nameSaving ? 'Saving...' : 'Save Name'}
            </button>
            {nameMsg && (
              <p className={nameMsg.type === 'success' ? styles.successMessage : styles.errorText}>
                {nameMsg.text}
              </p>
            )}
          </div>
        </motion.div>

        {/* Section 3: Password (only for password-based accounts) */}
        {user?.has_password !== false && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className={styles.settingsCard}
          >
            <div className={styles.cardContent}>
              <h2 className={styles.cardTitle}>
                <Lock className="inline w-5 h-5 mr-2" />
                PASSWORD CHANGE
              </h2>
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Current Password</label>
                <input
                  type="password"
                  className={styles.formInput}
                  value={currentPassword}
                  onChange={(e) => { setCurrentPassword(e.target.value); setPwMsg(null) }}
                  placeholder="Current password..."
                />
              </div>
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>New Password</label>
                <input
                  type="password"
                  className={styles.formInput}
                  value={newPassword}
                  onChange={(e) => { setNewPassword(e.target.value); setPwMsg(null) }}
                  placeholder="New password (min 8 chars)..."
                />
              </div>
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Confirm New Password</label>
                <input
                  type="password"
                  className={styles.formInput}
                  value={confirmPassword}
                  onChange={(e) => { setConfirmPassword(e.target.value); setPwMsg(null) }}
                  placeholder="Confirm new password..."
                />
              </div>
              <button
                className={styles.primaryButton}
                onClick={handleChangePassword}
                disabled={pwSaving || !currentPassword || !newPassword}
              >
                {pwSaving ? 'Changing...' : 'Change Password'}
              </button>
              {pwMsg && (
                <p className={pwMsg.type === 'success' ? styles.successMessage : styles.errorText}>
                  {pwMsg.text}
                </p>
              )}
            </div>
          </motion.div>
        )}

        {/* Section 4: Email Notifications */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 }}
          className={styles.settingsCard}
        >
          <div className={styles.cardContent}>
            <h2 className={styles.cardTitle}>
              <Bell className="inline w-5 h-5 mr-2" />
              NOTIFICATIONS
            </h2>
            <div className={styles.toggleGroup}>
              <span className={styles.toggleLabel}>
                {nlUnavailable ? 'Currently unavailable' : 'Email Newsletter'}
              </span>
              <div
                className={`${styles.toggle} ${subscribed ? styles.active : ''} ${(nlLoading || nlUnavailable || nlToggling) ? styles.disabled : ''}`}
                onClick={(!nlLoading && !nlUnavailable && !nlToggling) ? handleToggleNewsletter : undefined}
              />
            </div>
            <div className={styles.infoBox}>
              <p className={styles.infoText}>
                {`Receive updates about new features, ${t.course.lowerPlural}, and pirate ${t.lesson.lowerPlural}.`}
              </p>
            </div>
          </div>
        </motion.div>

        {/* Section 5: Referral Program */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className={styles.settingsCard}
        >
          <div className={styles.cardContent}>
            <h2 className={styles.cardTitle}>
              <Gift className="inline w-5 h-5 mr-2" />
              REFERRAL PROGRAM
            </h2>
            <div className={styles.infoBox}>
              <p className={styles.infoText}>
                Share your code — both you and your friend get 5 credits!
              </p>
            </div>
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>Your Referral Code</label>
              <div className={styles.inlineRow}>
                <input
                  type="text"
                  className={`${styles.formInput} ${styles.referralCodeDisplay}`}
                  value={user?.referral_code || ''}
                  readOnly
                />
                <button
                  className={`${styles.primaryButton} ${styles.compactButton}`}
                  onClick={() => {
                    if (user?.referral_code) {
                      navigator.clipboard.writeText(user.referral_code)
                      setReferralCopied(true)
                      setTimeout(() => setReferralCopied(false), 2000)
                    }
                  }}
                >
                  {referralCopied ? <><Check className="w-4 h-4" /> Copied</> : <><Copy className="w-4 h-4" /> Copy</>}
                </button>
              </div>
            </div>
            {referralStats && (
              <div className={styles.statsGrid}>
                <div className={styles.statItem}>
                  <p className={styles.statValue}>{referralStats.total_referrals}</p>
                  <p className={styles.statLabel}>Referrals</p>
                </div>
                <div className={styles.statItem}>
                  <p className={styles.statValue}>{referralStats.credits_earned}</p>
                  <p className={styles.statLabel}>Credits Earned</p>
                </div>
              </div>
            )}
            {!alreadyReferred && (
              <>
                <div className={`${styles.formGroup} mt-4`}>
                  <label className={styles.formLabel}>Were you referred? Enter their code</label>
                  <div className={styles.inlineRow}>
                    <input
                      type="text"
                      className={`${styles.formInput} ${styles.referralInput}`}
                      value={referralCodeInput}
                      onChange={(e) => { setReferralCodeInput(e.target.value.toUpperCase()); setReferralMsg(null) }}
                      placeholder="Enter referral code..."
                    />
                    <button
                      className={`${styles.primaryButton} ${styles.compactButton}`}
                      onClick={handleSubmitReferralCode}
                      disabled={referralSubmitting || !referralCodeInput.trim()}
                    >
                      {referralSubmitting ? 'Submitting...' : 'Submit'}
                    </button>
                  </div>
                </div>
                {referralMsg && (
                  <p className={referralMsg.type === 'success' ? styles.successMessage : styles.errorText}>
                    {referralMsg.text}
                  </p>
                )}
              </>
            )}
          </div>
        </motion.div>

        {/* Section 6: Delete Account */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className={styles.settingsCard}
        >
          <div className={styles.cardContent}>
            <h2 className={styles.cardTitle}>
              <Trash2 className="inline w-5 h-5 mr-2" />
              ABANDON SHIP
            </h2>
            <div className={styles.warningBox}>
              <p className={styles.warningText}>
                This action is permanent. Your account and all data will be deleted.
              </p>
            </div>
            {!showDeleteConfirm ? (
              <button
                className={styles.dangerButton}
                onClick={() => setShowDeleteConfirm(true)}
              >
                Delete Account
              </button>
            ) : (
              <>
                {user?.has_password !== false ? (
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>Confirm Password</label>
                    <input
                      type="password"
                      className={styles.formInput}
                      value={deletePassword}
                      onChange={(e) => { setDeletePassword(e.target.value); setDeleteError('') }}
                      placeholder="Enter your password to confirm..."
                    />
                  </div>
                ) : (
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>Type DELETE to confirm</label>
                    <input
                      type="text"
                      className={styles.formInput}
                      value={deleteConfirmText}
                      onChange={(e) => { setDeleteConfirmText(e.target.value); setDeleteError('') }}
                      placeholder="DELETE"
                    />
                  </div>
                )}
                <div className={styles.buttonGroup}>
                  <button
                    className={styles.dangerButton}
                    onClick={handleDeleteAccount}
                    disabled={deleteLoading || (user?.has_password !== false ? !deletePassword : deleteConfirmText !== 'DELETE')}
                  >
                    {deleteLoading ? 'Deleting...' : 'Confirm Delete'}
                  </button>
                  <button
                    className={styles.secondaryButton}
                    onClick={() => { setShowDeleteConfirm(false); setDeletePassword(''); setDeleteConfirmText(''); setDeleteError('') }}
                  >
                    Cancel
                  </button>
                </div>
                {deleteError && <p className={styles.errorText}>{deleteError}</p>}
              </>
            )}
          </div>
        </motion.div>

      </div>
    </div>
  )
}
